/**
 * AddressAutocomplete Component
 *
 * Smart address field with Google Places autocomplete
 * Auto-populates street, city, state, and zip when user selects an address
 *
 * Requires: VITE_GOOGLE_MAPS_API_KEY in .env file
 */

import { useRef, useEffect, useState } from 'react';

export default function AddressAutocomplete({
  street,
  city,
  state,
  zip,
  onStreetChange,
  onCityChange,
  onStateChange,
  onZipChange,
  onCountyChange,
  onAddressSelect
}) {
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);

  useEffect(() => {
    // Function to check if Google Maps API is loaded
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsGoogleMapsLoaded(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkGoogleMapsLoaded()) {
      return;
    }

    // Track if component is still mounted
    let isMounted = true;

    // If not loaded, set up interval to check periodically
    const intervalId = setInterval(() => {
      if (checkGoogleMapsLoaded()) {
        clearInterval(intervalId);
      }
    }, 100);

    // Cleanup interval after 10 seconds
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      if (isMounted && !window.google?.maps?.places) {
        console.warn('Google Maps API failed to load within 10 seconds');
      }
    }, 10000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    // Only initialize autocomplete once Google Maps is loaded
    if (!isGoogleMapsLoaded || !inputRef.current) {
      return;
    }

    // Initialize autocomplete
    try {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address']
      });

      // Listen for place selection
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();

        if (!place || !place.address_components) {
          return;
        }

        // Parse address components
        let streetNumber = '';
        let route = '';
        let cityName = '';
        let countyName = '';
        let stateName = '';
        let stateCode = '';
        let zipCode = '';

        place.address_components.forEach(component => {
          const types = component.types;

          if (types.includes('street_number')) {
            streetNumber = component.long_name;
          }
          if (types.includes('route')) {
            route = component.long_name;
          }
          if (types.includes('locality')) {
            cityName = component.long_name;
          }
          if (types.includes('administrative_area_level_2')) {
            // This is the county
            countyName = component.long_name.replace(/ County$/i, ''); // Remove "County" suffix if present
          }
          if (types.includes('administrative_area_level_1')) {
            stateCode = component.short_name;
            stateName = component.long_name;
          }
          if (types.includes('postal_code')) {
            zipCode = component.long_name;
          }
        });

        // Combine street number and route
        const fullStreet = `${streetNumber} ${route}`.trim();

        // Update all fields
        if (fullStreet) onStreetChange(fullStreet);
        if (cityName) onCityChange(cityName);
        if (stateCode) onStateChange(stateCode);
        if (zipCode) onZipChange(zipCode);
        if (countyName && onCountyChange) onCountyChange(countyName);

        // Call callback with full address data
        if (onAddressSelect) {
          onAddressSelect({
            street: fullStreet,
            city: cityName,
            county: countyName,
            state: stateCode,
            zip: zipCode,
            formatted: place.formatted_address
          });
        }
      });
    } catch (error) {
      console.error('Error initializing address autocomplete:', error);
    }

    // Cleanup
    return () => {
      if (autocompleteRef.current && window.google) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isGoogleMapsLoaded]);

  return (
    <div>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
        Street Address <span style={{ color: '#ef4444' }}>*</span>
      </label>
      <input
        ref={inputRef}
        type="text"
        className="input"
        value={street}
        onChange={(e) => onStreetChange(e.target.value)}
        placeholder="Start typing your address..."
        required
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        name="street-address-disabled"
        id="street-address-field"
      />
      <div style={{
        fontSize: '12px',
        color: '#64748b',
        marginTop: '6px'
      }}>
        💡 Start typing and select from suggestions to auto-fill city, state, and zip
      </div>
    </div>
  );
}
